import { useState, useEffect, useId, useRef } from 'react'
import { m } from 'motion/react'
import { ArrowLeft, ArrowRight, Globe } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/cn'
import type { IntakeFormData, OrgType } from '@/hooks/useIntakeWizard'

interface OrganizationStepProps {
  formData: IntakeFormData
  updateField: <K extends keyof IntakeFormData>(field: K, value: IntakeFormData[K]) => void
  onNext: () => void
  onBack: () => void
}

const ORG_TYPE_OPTIONS: { value: OrgType; label: string }[] = [
  { value: 'musician', label: 'Musician / Performer' },
  { value: 'festival', label: 'Festival / Competition' },
  { value: 'ensemble', label: 'Ensemble / Orchestra' },
  { value: 'teacher', label: 'Private Teacher / Studio' },
  { value: 'church', label: 'Church / Religious Organization' },
  { value: 'school', label: 'School / Educational Organization' },
  { value: 'other', label: 'Other' },
]

function normalizeUrl(url: string): string {
  if (!url.trim()) return ''
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`
  }
  return url
}

function isValidUrl(url: string): boolean {
  if (!url.trim()) return true // optional
  try {
    new URL(normalizeUrl(url))
    return true
  } catch {
    return false
  }
}

export function OrganizationStep({ formData, updateField, onNext, onBack }: OrganizationStepProps) {
  const [errors, setErrors] = useState<{ organization?: string; orgType?: string; website?: string }>({})
  const orgInputRef = useRef<HTMLInputElement>(null)
  const organizationId = useId()
  const orgTypeLabelId = useId()
  const websiteId = useId()

  useEffect(() => {
    if (!formData.organization) orgInputRef.current?.focus()
  }, [formData.organization])

  const validate = (): boolean => {
    const next: { organization?: string; orgType?: string; website?: string } = {}
    if (!formData.organization.trim()) next.organization = 'Please enter your organization name'
    if (!formData.orgType) next.orgType = 'Please select your organization type'
    if (!isValidUrl(formData.website)) next.website = 'Please enter a valid website URL'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    if (formData.website.trim()) {
      updateField('website', normalizeUrl(formData.website))
    }
    onNext()
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-xl space-y-8">
      <m.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground text-3xl font-bold md:text-4xl">Tell us about your organization</h2>
        <p className="text-muted-foreground text-lg">Just the basics, you can add more on our call.</p>
      </m.div>

      {/* Organization name */}
      <m.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <label htmlFor={organizationId} className="text-muted-foreground mb-1 block text-sm font-medium">
          Organization name
        </label>
        <input
          id={organizationId}
          ref={orgInputRef}
          type="text"
          placeholder="Riverside Chamber Orchestra"
          value={formData.organization}
          onChange={(e) => {
            updateField('organization', e.target.value)
            if (errors.organization) setErrors((prev) => ({ ...prev, organization: undefined }))
          }}
          className="border-border focus:border-primary w-full border-b-2 bg-transparent px-4 py-4 text-xl transition-colors focus:outline-none"
        />
        {errors.organization && <p className="mt-2 text-sm text-red-500">{errors.organization}</p>}
      </m.div>

      {/* Organization type */}
      <m.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <span id={orgTypeLabelId} className="text-muted-foreground mb-2 block text-sm font-medium">
          What kind of organization?
        </span>
        <div role="group" aria-labelledby={orgTypeLabelId} className="grid grid-cols-2 gap-2">
          {ORG_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                updateField('orgType', option.value)
                if (errors.orgType) setErrors((prev) => ({ ...prev, orgType: undefined }))
              }}
              className={cn(
                'w-full rounded-lg border-2 px-4 py-3 text-left text-sm transition-all',
                'hover:border-primary/50 hover:bg-primary/5',
                formData.orgType === option.value ? 'border-primary bg-primary/5 font-medium' : 'border-border'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        {errors.orgType && <p className="mt-2 text-sm text-red-500">{errors.orgType}</p>}
      </m.div>

      {/* Website (optional) */}
      <m.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <label htmlFor={websiteId} className="text-muted-foreground mb-1 block text-sm font-medium">
          Current website <span className="text-muted-foreground/70">(optional)</span>
        </label>
        <div className="relative">
          <Globe className="text-muted-foreground absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2" />
          <input
            id={websiteId}
            type="text"
            placeholder="www.yourorganization.com"
            value={formData.website}
            onChange={(e) => {
              updateField('website', e.target.value)
              if (errors.website) setErrors((prev) => ({ ...prev, website: undefined }))
            }}
            className="border-border focus:border-primary w-full border-b-2 bg-transparent py-4 pr-4 pl-12 text-xl transition-colors focus:outline-none"
          />
        </div>
        {errors.website && <p className="mt-2 text-sm text-red-500">{errors.website}</p>}
      </m.div>

      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        className="flex items-center gap-4"
      >
        <Button type="button" variant="ghost" size="lg" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Button type="submit" size="lg">
          Continue
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </m.div>

      <m.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-muted-foreground text-sm"
      >
        Step 2 of 5
      </m.p>
    </form>
  )
}
